import {gql, NetworkStatus} from '@apollo/client';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import BenchlingConfig from 'BenchlingConfig';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ProcessRealtimeUpdateSubscriptionWrapper from 'processes/ProcessRealtimeUpdateSubscriptionWrapper';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {PROCESS_ROUTER_NODE_DETAILS_FRAGMENT} from '../../ProcessFragments';
import {
  ProcessFlowchartDataQuery,
  ProcessFlowchartDataQueryVariables,
} from './__generated/ProcessFlowchartDataQuery';
import {
  ProcessFlowchartStructureQuery,
  ProcessFlowchartStructureQueryVariables,
} from './__generated/ProcessFlowchartStructureQuery';

type ProcessFlowchartStructure = ExtractByTypename<
  ProcessFlowchartStructureQuery['processFlowcharts'][number],
  'ProcessFlowchartNode'
>;

export const PROCESS_FLOWCHART_STRUCTURE_QUERY = gql`
  query ProcessFlowchartStructureQuery($flowchartIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processFlowcharts(ids: $flowchartIds) {
      ... on ProcessFlowchartNode {
        id
        nodeConfigs {
          id
          name
          nodeDetails {
            ... on ProcessTaskNodeDetailsNode {
              id
              type
              schema {
                ... on ProcessTaskSchemaNode {
                  id
                  name
                }
                ...InaccessibleInfoGraphQL
              }
            }
            ... on ProcessRouterNodeDetailsNode {
              ...ProcessRouterNodeDetails
            }
            ... on ProcessRootNodeDetailsNode {
              ...ProcessRootNodeDetailsGraphQL
            }
            ... on ProcessOutputNodeDetailsNode {
              ...ProcessOutputNodeDetailsGraphQL
            }
            ... on ProcessEndNodeDetailsNode {
              ...ProcessEndNodeDetailsGraphQL
            }
          }
          inboundSchemaMappers {
            id
            toNodeConfig {
              id
            }
            schemaMapperConfig {
              id
            }
            schemaMapperDependencies {
              id
              fromNodeConfig {
                id
              }
            }
          }
        }
        edgeConfigs {
          id
          fromNodeConfig {
            id
          }
          toNodeConfig {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${PROCESS_ROUTER_NODE_DETAILS_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcessRootNodeDetails').fragment}
  ${getFragmentWrapper('ProcessOutputNodeDetails').fragment}
  ${getFragmentWrapper('ProcessEndNodeDetails').fragment}
`;

const EXECUTION_FLOWCHART_DATA_FRAGMENT = gql`
  fragment ExecutionFlowchartData on ProcessTaskExecutionFlowchartData {
    flowchartProcesses {
      id
      nodeConfig {
        id
      }
    }
    flowchartTasks {
      task {
        id
        process {
          id
        }
        status {
          id
          statusType
        }
        flowchartPredecessorNodes {
          id
        }
      }
      nodeConfig {
        id
        nodeDetails {
          ... on ProcessTaskNodeDetailsNode {
            id
            type
            schema {
              ... on ProcessTaskSchemaNode {
                id
                name
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ... on ProcessRootNodeDetailsNode {
            ...ProcessRootNodeDetailsGraphQL
          }
          ... on ProcessRouterNodeDetailsNode {
            ...ProcessRouterNodeDetails
          }
          ... on ProcessOutputNodeDetailsNode {
            ...ProcessOutputNodeDetailsGraphQL
          }
          ... on ProcessEndNodeDetailsNode {
            ...ProcessEndNodeDetailsGraphQL
          }
        }
      }
    }
  }
`;

/**
 * This query is run after the one above. Starting with the tasks in the root process, it follows their
 * trails, retrieving all tasks that have a root task in this list. This is used to summarize all the
 * data for all tasks in a flowchart (for now, that means the statuses and the task groups).
 */
export const PROCESS_FLOWCHART_DATA_FRAGMENT = gql`
  fragment ProcessFlowchartDataInfo on ProcessTaskNode {
    id
    status {
      id
      statusType
    }
    outputs {
      id
      process {
        id
        nodeConfig {
          id
        }
      }
      flowchartPredecessorNodes {
        id
      }
    }
    isMappingInProgressForFlowchartRootedAtTask
    executionFlowchartData {
      ...ExecutionFlowchartData
    }
  }
  ${getFragmentWrapper('ProcessOutputNodeDetails').fragment}
  ${getFragmentWrapper('ProcessRootNodeDetails').fragment}
  ${getFragmentWrapper('ProcessEndNodeDetails').fragment}
  ${PROCESS_ROUTER_NODE_DETAILS_FRAGMENT}
  ${EXECUTION_FLOWCHART_DATA_FRAGMENT}
`;

export const PROCESS_FLOWCHART_DATA_QUERY = gql`
  query ProcessFlowchartDataQuery($processTaskIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTasks(ids: $processTaskIds) {
      ... on ProcessTaskNode {
        ...ProcessFlowchartDataInfo
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${PROCESS_FLOWCHART_DATA_FRAGMENT}
`;

/**
 * This component executes two queries. The first query gets executed on
 * a polling loop, to ensure we sync with new server-side data that's been set asynchronously.
 * (A typical example: when a downstream task status is set to Invalid, the root task is set
 * to Cancelled. The new root status gets picked up during the poll.)
 *
 * Additionally, this component caches the query results. This prevents the ReactFlow library
 * from re-rendering the same flowchart with identical data.
 */
export function PollingFlowchartWrapper(props: {
  flowchartIds: Array<string>;
  processTaskIds: Array<string>;
  children: (
    flowcharts: Array<ProcessFlowchartStructure>,
    flowchartData: ProcessFlowchartDataQuery,
    forceRefetch: () => void
  ) => JSX.Element | null;
}): JSX.Element | null {
  const [flowchartCache, setFlowchartCache] = useState<Array<ProcessFlowchartStructure> | null>(null);
  const [flowchartDataCache, setFlowchartDataCache] = useState<ProcessFlowchartDataQuery | null>(null);

  const flowchartStructureResults = useBenchlingQuery<
    ProcessFlowchartStructureQuery,
    ProcessFlowchartStructureQueryVariables
  >(PROCESS_FLOWCHART_STRUCTURE_QUERY, {
    variables: {flowchartIds: props.flowchartIds},
  });

  // Do not use this value directly except for introspection. Use the cached value in state for rendering.
  const _flowchartDataResults = useBenchlingQuery<
    ProcessFlowchartDataQuery,
    ProcessFlowchartDataQueryVariables
  >(PROCESS_FLOWCHART_DATA_QUERY, {
    variables: {processTaskIds: props.processTaskIds},
    // This query benefits tremendously from live updates. We'll get new tasks, new statuses, etc.
    pollInterval: BenchlingConfig.WORKFLOWS_FLOWCHART_VIEWER_POLL_INTERVAL_MS,
    renderCachedDataWhenRefetching: true,
  });

  if (
    flowchartStructureResults.type !== QueryResultType.SUCCESS ||
    (_flowchartDataResults.type !== QueryResultType.SUCCESS &&
      _flowchartDataResults.networkStatus === NetworkStatus.refetch)
  ) {
    return <CenteredHorizontalThrobber />;
  }

  if (
    _flowchartDataResults.type === QueryResultType.SUCCESS &&
    !isEqual(flowchartDataCache, _flowchartDataResults.data)
  ) {
    setFlowchartDataCache(_flowchartDataResults.data);
  }

  const _flowcharts = flowchartStructureResults.data.processFlowcharts.filter(
    (flowchart) => !isInaccessible(flowchart)
  ) as Array<ProcessFlowchartStructure>;
  if (!isEqual(flowchartCache, _flowcharts)) {
    setFlowchartCache(_flowcharts);
  }

  const forceRefetch = (): void => {
    setFlowchartCache(null);
    setFlowchartDataCache(null);
    _flowchartDataResults.client.resetStore();
  };

  if (flowchartDataCache == null || flowchartCache == null) {
    return <CenteredHorizontalThrobber />;
  }

  return props.children(flowchartCache, flowchartDataCache, forceRefetch);
}

export function NonPollingFlowchartWrapper(props: {
  flowchartIds: Array<string>;
  processTaskIds: Array<string>;
  refetchParent: () => Promise<unknown>;
  children: (
    flowcharts: Array<ProcessFlowchartStructure>,
    flowchartData: ProcessFlowchartDataQuery,
    forceRefetch: () => void
  ) => JSX.Element | null;
}): JSX.Element | null {
  const flowchartStructureResults = useBenchlingQuery<
    ProcessFlowchartStructureQuery,
    ProcessFlowchartStructureQueryVariables
  >(PROCESS_FLOWCHART_STRUCTURE_QUERY, {
    renderCachedDataWhenRefetching: true,
    variables: {flowchartIds: props.flowchartIds},
  });

  const flowchartDataResults = useBenchlingQuery<
    ProcessFlowchartDataQuery,
    ProcessFlowchartDataQueryVariables
  >(PROCESS_FLOWCHART_DATA_QUERY, {
    variables: {processTaskIds: props.processTaskIds},
    renderCachedDataWhenRefetching: true,
    fetchPolicy: 'network-only',
  });

  if (
    flowchartStructureResults.type !== QueryResultType.SUCCESS ||
    flowchartDataResults.type !== QueryResultType.SUCCESS
  ) {
    return <CenteredHorizontalThrobber />;
  }

  const flowcharts = flowchartStructureResults.data.processFlowcharts.filter(
    (flowchart): flowchart is ProcessFlowchartStructure => !isInaccessible(flowchart)
  );

  return props.children(flowcharts, flowchartDataResults.data, () => {
    props.refetchParent();
    flowchartStructureResults.refetch();
    flowchartDataResults.refetch();
  });
}

export interface LiveUpdatingTaskFlowchartWrapperProps {
  parentId: string;
  flowchartIds: Array<string>;
  processTaskIds: Array<string>;
  children: (
    flowcharts: Array<ProcessFlowchartStructure>,
    flowchartData: ProcessFlowchartDataQuery,
    forceRefetch: () => void
  ) => JSX.Element | null;
  refetch: () => Promise<unknown>;
}

export default function LiveUpdatingTaskFlowchartWrapper(
  props: LiveUpdatingTaskFlowchartWrapperProps
): JSX.Element {
  if (BenchlingConfig.WORKFLOWS_ENABLE_LIVE_UPDATING_UI) {
    return (
      <NonPollingFlowchartWrapper
        flowchartIds={props.flowchartIds}
        processTaskIds={props.processTaskIds}
        refetchParent={props.refetch}
      >
        {(flowcharts, flowchartData, forceRefetch) => {
          let hasIncompleteData = false;
          if (flowchartData.processTasks.length > 0 && !isInaccessible(flowchartData.processTasks[0])) {
            hasIncompleteData = flowchartData.processTasks[0].isMappingInProgressForFlowchartRootedAtTask;
          }
          return (
            <ProcessRealtimeUpdateSubscriptionWrapper
              apiId={props.parentId}
              refetch={forceRefetch}
              refetchOnMessagesFromSameClient={true}
              hasIncompleteDataShouldRefetch={hasIncompleteData}
            >
              {props.children(flowcharts, flowchartData, forceRefetch)}
            </ProcessRealtimeUpdateSubscriptionWrapper>
          );
        }}
      </NonPollingFlowchartWrapper>
    );
  } else {
    return (
      <PollingFlowchartWrapper flowchartIds={props.flowchartIds} processTaskIds={props.processTaskIds}>
        {(flowcharts, flowchartData, forceRefetch) => props.children(flowcharts, flowchartData, forceRefetch)}
      </PollingFlowchartWrapper>
    );
  }
}
